import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import type { QueueJobDetail, QueueJobState, QueueJobSummary, QueueName } from "@/lib/admin-queues";
import type { ManualTasksResponse } from "@/lib/scheduled-tasks";
import type { TaskActivityRun } from "@/lib/task-runs/present";
import type { TaskRunDetail, TaskRunStatus, TaskRunSummary } from "@/lib/task-runs/types";

/** The first page has no cursor. */
const FIRST_PAGE: string | undefined = undefined;
const RUN_POLL_MS = 1_500;
const PAGE_SIZE = 25;

/** Reads a JSON answer, turning any failure into the user-facing message. */
async function readJson<T>(response: Response, message: string): Promise<T> {
  if (!response.ok) throw new Error(message);

  // SAFETY: every caller reads one of this app's own admin endpoints and names its answer shape.
  return (await response.json()) as T;
}

/** The scheduled tasks with their state, refreshed while the page is open. */
export function scheduledTasksQueryOptions() {
  return queryOptions({
    queryKey: ["admin", "scheduled-tasks"],
    refetchInterval: 5_000,
    queryFn: async (): Promise<ManualTasksResponse> =>
      readJson(await fetch("/api/admin/tasks"), "Impossible de charger les tâches planifiées."),
  });
}

export interface TaskRunsActivity {
  windowEnd: number;
  runs: TaskActivityRun[];
  truncated: boolean;
}

/** Every run of the last `hours`, compact: the console's charts and counts. */
export function taskRunsActivityQueryOptions(hours: number) {
  return queryOptions({
    queryKey: ["admin", "task-runs", "activity", hours],
    refetchInterval: 30_000,
    queryFn: async ({ signal }): Promise<TaskRunsActivity> =>
      readJson(
        await fetch(`/api/admin/task-runs/activity?hours=${hours}`, { signal }),
        "Impossible de charger l'activité des tâches.",
      ),
  });
}

export interface TaskRunsFilter {
  name?: string;
  status?: TaskRunStatus;
}

/** Run history, newest first, paged by the `startedAt` of the last run seen. */
export function taskRunsInfiniteQueryOptions(filter: TaskRunsFilter) {
  return infiniteQueryOptions({
    queryKey: ["admin", "task-runs", "list", filter.name ?? null, filter.status ?? null],
    initialPageParam: FIRST_PAGE,
    queryFn: async ({ pageParam }): Promise<{ runs: TaskRunSummary[]; hasMore: boolean }> => {
      const params = new URLSearchParams({ limit: String(PAGE_SIZE) });

      if (filter.name) params.set("name", filter.name);

      if (filter.status) params.set("status", filter.status);

      if (pageParam) params.set("before", pageParam);

      return readJson(
        await fetch(`/api/admin/task-runs?${params}`),
        "Impossible de charger l'historique.",
      );
    },
    getNextPageParam: (last) => (last.hasMore ? last.runs.at(-1)?.startedAt : undefined),
    refetchInterval: 10_000,
  });
}

/** One task's newest runs, newest first: the sheet's duration chart past the list's 20. */
export function recentTaskRunsQueryOptions(name: string, limit: number) {
  return queryOptions({
    queryKey: ["admin", "task-runs", "recent", name, limit],
    refetchInterval: 30_000,
    queryFn: async ({ signal }): Promise<TaskRunSummary[]> => {
      const params = new URLSearchParams({ name, limit: String(limit) });

      const { runs } = await readJson<{ runs: TaskRunSummary[] }>(
        await fetch(`/api/admin/task-runs?${params}`, { signal }),
        "Impossible de charger les exécutions.",
      );

      return runs;
    },
  });
}

/** One run; polled while it is running so the sheet follows it live. */
export function taskRunQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["admin", "task-runs", "detail", id],
    queryFn: async (): Promise<TaskRunDetail> => {
      const { run } = await readJson<{ run: TaskRunDetail }>(
        await fetch(`/api/admin/task-runs/${encodeURIComponent(id)}`),
        "Impossible de charger l'exécution.",
      );

      return run;
    },
    refetchInterval: (query) => (query.state.data?.status === "running" ? RUN_POLL_MS : false),
  });
}

/** The jobs of one queue in one state, newest first, straight from Redis. */
export function queueJobsQueryOptions(queue: QueueName, state: QueueJobState) {
  return queryOptions({
    queryKey: ["admin", "queues", queue, "jobs", state],
    queryFn: async (): Promise<{ jobs: QueueJobSummary[]; hasMore: boolean }> =>
      readJson(
        await fetch(`/api/admin/queues/${queue}/jobs?state=${state}&limit=${PAGE_SIZE}`),
        "Impossible de charger les tâches de la file.",
      ),
  });
}

export function queueJobQueryOptions(queue: QueueName, jobId: string) {
  return queryOptions({
    queryKey: ["admin", "queues", queue, "job", jobId],
    queryFn: async (): Promise<QueueJobDetail> => {
      const response = await fetch(`/api/admin/queues/${queue}/jobs/${encodeURIComponent(jobId)}`);

      if (response.status === 404) {
        throw new Error(
          "Cette tâche n'est plus dans Redis (24 h après la fin, 7 jours si échouée).",
        );
      }

      const { job } = await readJson<{ job: QueueJobDetail }>(
        response,
        "Impossible de charger la tâche.",
      );

      return job;
    },
    // Retries would only repeat a 404.
    retry: false,
  });
}
